import { useEffect, useState } from 'react';
import { addDays, format, formatDistanceToNow, parseISO } from 'date-fns';

import { Button, Card } from '@/design-system/primitives';
import { supportChat } from '@/components/app-shell/support/supportChatUi';
import { track } from '@/lib/analytics';
import {
  useChangeSubscriptionPlan,
  useMySubscriptions,
  usePauseSubscription,
  useRescheduleSubscription,
  useResumeSubscription,
  useSubscriptionPlanOptions,
} from '@/lib/api/hooks';

import { LabSubscriptionCards } from './LabSubscriptionCards';
import { useLabSubscriptions } from '@/lib/api/labHooks';
import { CancelFlowDialog } from './CancelFlowDialog';
import { memberPriceCents, useMembership } from '@/lib/membership';
import type {
  MeSubscription,
  PlanFrequencyOption,
  UpcomingFill,
} from '@/lib/api/hooks';

const RESCHEDULE_MIN_DAYS = 7;
const RESCHEDULE_MAX_DAYS = 120;
const PAUSE_DEFAULT_DAYS = 28;
const PAUSE_MAX_DAYS = 90;

/**
 * Patient-facing subscription manager.
 *
 * Lives in Account → "Subscriptions". Each active sub gets one card
 * with status, next renewal date, upcoming fills, and the four
 * lifecycle actions (cancel, pause, resume, reschedule).
 *
 * Cancellation = cancel-at-period-end. Patient keeps every fill they've
 * paid for; sub terminates when the period closes; they can hit Resume
 * before that to undo. Pause defaults to 28 days (one cycle) and caps
 * at 90. Reschedule lets them push the next bill to any date 7–120 days
 * from today.
 */
export function SubscriptionsSection({ onReady }: { onReady?: () => void } = {}) {
  const { data, isLoading, isError, error } = useMySubscriptions();
  const labs = useLabSubscriptions();
  const labSubs = labs.data?.subscriptions ?? [];
  useEffect(() => {
    if (!isLoading && !labs.isLoading) onReady?.();
  }, [isLoading, labs.isLoading, onReady]);
  // Active subscriptions first, cancelled ones sink to the bottom.
  const subs = [...(data?.subscriptions ?? [])].sort(
    (a, b) =>
      Number(a.status === 'cancelled') - Number(b.status === 'cancelled'),
  );
  // Recoverable dunning and terminal unpaid are different states. Stripe may
  // retry a past-due invoice, while an unpaid subscription has already ended
  // its automatic billing lifecycle and needs a deliberate recovery review.
  const anyPastDue = subs.some(
    (s) => s.past_due && s.status !== 'payment_failed',
  );
  const anyPaymentFailed = subs.some((s) => s.status === 'payment_failed');

  return (
    <Card padding="lg">
      <Card.Header>
        <Card.Title>Subscriptions</Card.Title>
      </Card.Header>
      <Card.Body>
        {anyPastDue && (
          <div
            role="alert"
            className="mb-12 border-l-4 px-15 py-12"
            style={{
              background: 'var(--warning-wash)',
              borderColor: 'var(--warning)',
            }}
          >
            <p
              className="type-body-sm font-semibold"
              style={{ color: 'var(--warning)' }}
            >
              Payment issue — your next shipment is on hold.
            </p>
            <p className="type-body-sm text-ink mt-2">
              We couldn’t process your latest renewal payment. Your next
              shipment will be <strong>delayed</strong> until your card is
              updated — and your subscription will be{' '}
              <strong>cancelled</strong> if payment isn’t resolved within ~2
              weeks of the first failed charge. Update your card under{' '}
              <strong>Payment methods</strong>; the new card becomes your
              default and the payment retries automatically.
            </p>
          </div>
        )}
        {anyPaymentFailed && (
          <div
            role="alert"
            className="mb-12 border-l-4 px-15 py-12"
            style={{
              background: 'var(--danger-wash)',
              borderColor: 'var(--danger)',
            }}
          >
            <p
              className="type-body-sm font-semibold"
              style={{ color: 'var(--danger)' }}
            >
              Payment failed — this subscription is inactive.
            </p>
            <p className="type-body-sm text-ink mt-2">
              The renewal payment could not be completed, so future billing
              and fulfillment are stopped. Updating a saved card does not
              automatically restart this subscription. Message support to
              review the available recovery options.
            </p>
            <Button
              size="sm"
              variant="secondary"
              className="mt-10"
              onClick={() => supportChat.open()}
            >
              Message billing support
            </Button>
          </div>
        )}
        {isLoading ? (
          <p className="type-body-sm text-ink-muted">Loading…</p>
        ) : isError ? (
          <p className="type-body-sm text-status-danger">
            Couldn’t load subscriptions: {String((error as Error)?.message || error)}
          </p>
        ) : subs.length > 0 ? (
          <div className="flex flex-col gap-15">
            {subs.map((s) => (
              <SubscriptionCard key={s.stripe_subscription_id} sub={s} />
            ))}
          </div>
        ) : null}
        {labs.isLoading && (
          <p className="type-label-xs text-ink-muted mt-15" role="status">
            Fetching subscriptions…
          </p>
        )}
        {labs.isError && (
          <p className="type-body-sm mt-15" role="alert">
            Some subscription details could not load.{' '}
            <button className="underline min-h-44" onClick={() => void labs.refetch()}>
              Try again
            </button>
          </p>
        )}
        {labSubs.length > 0 && (
          <div className={subs.length ? 'mt-15' : undefined}>
            <LabSubscriptionCards subscriptions={labSubs} />
          </div>
        )}
        {!isLoading && !isError && !labs.isLoading && !labs.isError &&
          subs.length === 0 && labSubs.length === 0 && (
            <p className="type-body-sm text-ink-muted">
              You don’t have any active subscriptions yet.
            </p>
          )}
      </Card.Body>
    </Card>
  );
}

function SubscriptionCard({ sub }: { sub: MeSubscription }) {
  const [mode, setMode] = useState<
    'idle' | 'reschedule' | 'pause' | 'change-plan'
  >('idle');
  const [cancelFlowOpen, setCancelFlowOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pause = usePauseSubscription();
  const resume = useResumeSubscription();
  const reschedule = useRescheduleSubscription();
  const changePlan = useChangeSubscriptionPlan();

  const next = sub.next_renewal_at ? parseISO(sub.next_renewal_at) : null;
  const nextLabel = next ? format(next, 'MMM d, yyyy') : '—';
  const nextRelative = next ? formatDistanceToNow(next, { addSuffix: true }) : null;

  const planLabel =
    sub.plan_cycles === 1
      ? 'Monthly (every 28 days)'
      : sub.plan_cycles === 3
        ? 'Quarterly (every 84 days)'
        : `Every ${sub.plan_cycles * 28} days`;
  const price =
    sub.unit_amount_cents != null
      ? `$${(sub.unit_amount_cents / 100).toFixed(2)}${sub.currency ? ` ${sub.currency.toUpperCase()}` : ''}`
      : '—';
  // A save-offer (or any) discount on the sub: show what the next
  // invoice really charges so patients can see the discount landed.
  const discountedNext =
    sub.next_renewal_amount_cents != null &&
    sub.unit_amount_cents != null &&
    sub.next_renewal_amount_cents < sub.unit_amount_cents
      ? sub.next_renewal_amount_cents
      : null;
  const discountPct =
    discountedNext != null && sub.unit_amount_cents
      ? Math.round((1 - discountedNext / sub.unit_amount_cents) * 100)
      : null;
  // Only the permanent member coupon applies: every renewal is this price,
  // so "then $<full price>" would be wrong.
  const { percent: memberPercent } = useMembership();
  const memberPriceOnly =
    discountedNext != null &&
    memberPercent > 0 &&
    sub.unit_amount_cents != null &&
    // ±1¢: the backend estimate rounds half-to-even.
    Math.abs(discountedNext - memberPriceCents(sub.unit_amount_cents, memberPercent)) <= 1;

  const productLabel = sub.product_name || sub.product_id || 'Subscription';

  async function handle(promise: Promise<unknown>) {
    setError(null);
    try {
      await promise;
      setMode('idle');
    } catch (e) {
      setError(String((e as Error)?.message || e));
    }
  }

  const isCancelled = sub.status === 'cancelled';
  const isPaymentFailed = sub.status === 'payment_failed';
  // Treat either status or explicit provenance as paused. During a rolling
  // deploy an older/newer backend may omit can_self_resume or briefly return
  // inconsistent display fields; self-service resume is allowed only by an
  // explicit true from the server.
  const isPaused = sub.status === 'paused' || sub.paused === true;
  const canSelfResume = isPaused && sub.can_self_resume === true;
  const requiresPauseRecovery = isPaused && !canSelfResume;
  const actionsLocked = isCancelled || isPaymentFailed || requiresPauseRecovery;

  return (
    <div
      className={`border border-line-subtle p-15${isCancelled ? ' bg-surface-subtle opacity-45' : ''}`}
    >
      <div className="flex items-start justify-between gap-x-15 gap-y-8 flex-wrap mb-12">
        {/* min-w floor keeps long names ("Compounded GLP-1/GIP") readable:
            when the name + pill don't fit on one row the PILL wraps below
            instead of the flex algorithm crushing the title into a
            one-character-per-line column. */}
        <div className="flex flex-col gap-2 min-w-[16rem] flex-1">
          <h3 className="type-title-md break-words">{productLabel}</h3>
          <div className="type-label-sm text-ink-muted">{planLabel}</div>
          {sub.dose_contents && <div className="type-body-sm text-ink-secondary">{sub.dose_contents}</div>}
        </div>
        <StatusPill sub={sub} />
      </div>

      <dl className="grid grid-cols-2 gap-x-20 gap-y-8 type-body-sm mb-12">
        {isPaymentFailed ? (
          <Field
            label="Subscription"
            value="Inactive"
            sub="No automatic renewal is scheduled"
          />
        ) : requiresPauseRecovery ? (
          <Field
            label="Subscription"
            value="Paused"
            sub="Billing support must review this pause"
          />
        ) : (
          <Field
            label={
              sub.status === 'paused'
                ? 'Resumes'
                : sub.status === 'cancel_at_period_end'
                  ? 'Cancels on'
                  : sub.status === 'cancelled'
                    ? 'Cancelled'
                    : 'Next renewal'
            }
            value={nextLabel}
            sub={nextRelative ?? undefined}
          />
        )}
        {isPaymentFailed ? (
          <Field
            label="Recovery"
            value="Support review needed"
            sub="A saved card change alone will not restart it"
          />
        ) : requiresPauseRecovery ? (
          <Field
            label="Recovery"
            value="Support review needed"
            sub="Self-service resume is unavailable"
          />
        ) : sub.status === 'cancel_at_period_end' || sub.status === 'cancelled' ? (
          // A cancelled plan advertising a renewal price reads like an
          // upcoming charge. Say the thing patients actually want to know.
          <Field
            label="Billing"
            value="No more charges"
            sub="You won’t be billed again"
          />
        ) : discountedNext != null ? (
          // Compact on purpose: the value stays a short dollar figure so
          // the two-column grid doesn't wrap awkwardly on mobile.
          <Field
            label="Price"
            value={`$${(discountedNext / 100).toFixed(2)}`}
            sub={memberPriceOnly
              ? `Member price · ${memberPercent}% off every renewal`
              : `${discountPct != null ? `${discountPct}% off next renewal` : 'discount applied'}, then $${sub.unit_amount_cents != null ? (sub.unit_amount_cents / 100).toFixed(2) : '—'}`}
          />
        ) : (
          <Field label="Price" value={price} />
        )}
      </dl>

      {!isPaymentFailed && sub.upcoming_fills.length > 0 && (
        <details className="mb-12">
          <summary className="type-label-sm uppercase tracking-tight text-ink-muted cursor-pointer">
            Upcoming fills ({sub.upcoming_fills.length})
          </summary>
          <ul className="mt-8 flex flex-col gap-6">
            {sub.upcoming_fills.map((f) => (
              <FillRow key={f.id} f={f} />
            ))}
          </ul>
        </details>
      )}

      {!actionsLocked && mode === 'idle' && (
        <div className="flex flex-wrap gap-8">
          {canSelfResume || sub.status === 'cancel_at_period_end' ? (
            <Button
              size="sm"
              onClick={() => handle(resume.mutateAsync({ subId: sub.stripe_subscription_id }))}
              loading={resume.isPending}
            >
              {canSelfResume ? 'Resume now' : 'Undo cancel'}
            </Button>
          ) : (
            <>
              <Button size="sm" variant="secondary" onClick={() => setMode('pause')}>
                Pause
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setMode('reschedule')}>
                Change renewal date
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setMode('change-plan')}
              >
                Switch plan length
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  track('cancel_flow_opened', {
                    sub_id: sub.stripe_subscription_id,
                    product_id: sub.product_id,
                  });
                  setCancelFlowOpen(true);
                }}
              >
                Cancel subscription
              </Button>
            </>
          )}
        </div>
      )}

      {requiresPauseRecovery && (
        <Button size="sm" variant="secondary" onClick={() => supportChat.open()}>
          Message billing support
        </Button>
      )}

      {!actionsLocked && mode === 'pause' && (
        <PausePanel
          subId={sub.stripe_subscription_id}
          loading={pause.isPending}
          onConfirm={(days) =>
            handle(pause.mutateAsync({ subId: sub.stripe_subscription_id, days }))
          }
          onCancel={() => setMode('idle')}
        />
      )}

      {!actionsLocked && (
        <CancelFlowDialog
          sub={sub}
          open={cancelFlowOpen}
          onOpenChange={setCancelFlowOpen}
        />
      )}

      {!actionsLocked && mode === 'change-plan' && (
        <ChangePlanPanel
          subId={sub.stripe_subscription_id}
          loading={changePlan.isPending}
          onConfirm={(frequency) =>
            handle(
              changePlan.mutateAsync({
                subId: sub.stripe_subscription_id,
                frequency,
              }),
            )
          }
          onCancel={() => setMode('idle')}
        />
      )}

      {!actionsLocked && mode === 'reschedule' && (
        <ReschedulePanel
          loading={reschedule.isPending}
          currentRenewalDate={sub.current_period_end ?? null}
          onConfirm={(date) =>
            handle(
              reschedule.mutateAsync({
                subId: sub.stripe_subscription_id,
                nextRenewalDate: date,
              }),
            )
          }
          onCancel={() => setMode('idle')}
        />
      )}

      {error && (
        <p className="mt-12 type-body-sm text-status-danger">{error}</p>
      )}
    </div>
  );
}

export function StatusPill({ sub }: { sub: MeSubscription }) {
  let label = 'Active';
  let cls = 'bg-status-success-subtle text-status-success';
  if (sub.status === 'paused' || sub.paused === true) {
    // Just "Paused" — the resume date already renders as the card's
    // "Resumes" field, and the long pill was crushing product names on
    // mobile.
    label = 'Paused';
    cls = 'bg-status-warning-subtle text-status-warning';
  } else if (sub.status === 'cancel_at_period_end') {
    label = sub.current_period_end
      ? `Ends ${format(parseISO(sub.current_period_end), 'MMM d')}`
      : 'Ending soon';
    cls = 'bg-status-warning-subtle text-status-warning';
  } else if (sub.status === 'cancelled') {
    label = 'Cancelled';
    cls = 'bg-surface-subtle text-ink';
  } else if (sub.status === 'payment_failed') {
    label = 'Payment failed';
    cls = 'bg-status-danger-subtle text-status-danger';
  }
  return (
    <span
      className={` ${cls} type-label-sm uppercase tracking-tight px-10 py-4 whitespace-nowrap`}
    >
      {label}
    </span>
  );
}

function FillRow({ f }: { f: UpcomingFill }) {
  const when = f.scheduled_for ? format(parseISO(f.scheduled_for), 'MMM d, yyyy') : '—';
  const sub =
    f.status === 'submitted'
      ? 'Already shipped'
      : f.status === 'paused'
        ? 'Paused'
        : `Ships ${when}`;
  return (
    <li className="flex items-center justify-between gap-15">
      <span className="type-body-sm">
        Fill {f.shipment_index} of {f.total_shipments}
        {f.dose_contents ? ` · ${f.dose_contents}` : f.container_qty > 1 ? ` · ${f.container_qty} containers` : ''}
      </span>
      <span className="type-label-sm text-ink-muted">{sub}</span>
    </li>
  );
}

function PausePanel({
  subId,
  loading,
  onConfirm,
  onCancel,
}: {
  subId: string;
  loading: boolean;
  onConfirm: (days: number) => void;
  onCancel: () => void;
}) {
  const [days, setDays] = useState(PAUSE_DEFAULT_DAYS);
  void subId;
  return (
    <div className="mt-12 bg-surface-subtle px-15 py-12">
      <p className="type-body-sm mb-10">
        Pause billing + shipments for how long?
      </p>
      <div className="flex flex-wrap items-center gap-10">
        <input
          type="number"
          min={1}
          max={PAUSE_MAX_DAYS}
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="w-80 border border-line-default px-10 py-8 type-body-sm"
        />
        <span className="type-body-sm">days (max {PAUSE_MAX_DAYS})</span>
      </div>
      <p className="type-label-sm text-ink-muted mt-8">
        Defaults to one full 28-day cycle. We’ll push every scheduled fill back
        and you won’t be billed during the pause.
      </p>
      <div className="flex gap-8 mt-12">
        <Button size="sm" onClick={() => onConfirm(days)} loading={loading}>
          Confirm pause
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Never mind
        </Button>
      </div>
    </div>
  );
}

function ChangePlanPanel({
  subId,
  loading,
  onConfirm,
  onCancel,
}: {
  subId: string;
  loading: boolean;
  onConfirm: (frequency: PlanFrequencyOption) => void;
  onCancel: () => void;
}) {
  const { data, isLoading, isError } = useSubscriptionPlanOptions(subId, true);
  const [selected, setSelected] = useState<PlanFrequencyOption | null>(null);
  const options = data?.options ?? [];
  const switchable = options.filter((o) => !o.is_current);
  const effective = data?.effective_at
    ? format(parseISO(data.effective_at), 'MMM d, yyyy')
    : 'your next renewal';
  const chosen = options.find((o) => o.frequency === selected) ?? null;
  // Members keep the member coupon through a plan-length switch, so quote
  // what they will actually be billed.
  const { percent: memberPercent } = useMembership();
  const billed = (cents: number) =>
    memberPercent > 0 ? memberPriceCents(cents, memberPercent) : cents;
  const dollars = (cents: number) =>
    cents % 100 ? (cents / 100).toFixed(2) : String(cents / 100);
  const memberNote = memberPercent > 0 ? ' (member price)' : '';
  // Savings vs monthly from the prices shown: for members, both sides at the
  // member price (scaling the list savings can drift a dollar from them).
  const monthlyOption = options.find((o) => o.frequency === 'monthly') ?? null;
  const savingsCents = (o: (typeof options)[number]) =>
    memberPercent > 0 && monthlyOption
      ? Math.round(billed(monthlyOption.renewal_cents) * (o.interval_days / 28)) -
        billed(o.renewal_cents)
      : o.savings_vs_monthly_cents;

  return (
    <div className="mt-12 bg-surface-subtle px-15 py-12">
      {isLoading ? (
        <p className="type-body-sm text-ink-muted">Loading plan options…</p>
      ) : isError || switchable.length === 0 ? (
        <p className="type-body-sm text-ink-muted">
          No other plan lengths are available for this treatment.
        </p>
      ) : (
        <>
          <p className="type-body-sm mb-10">
            Pick a new plan length. The change applies at your next renewal
            ({effective}) — nothing changes about your current cycle or its
            shipments.
          </p>
          <div className="flex flex-col gap-8">
            {switchable.map((o) => {
              const months = o.interval_days / 28;
              const perMonth = Math.round(billed(o.renewal_cents) / months / 100);
              return (
                <label
                  key={o.frequency}
                  className="flex items-center gap-10 type-body-sm cursor-pointer"
                >
                  <input
                    type="radio"
                    name={`plan-${subId}`}
                    checked={selected === o.frequency}
                    onChange={() => setSelected(o.frequency)}
                  />
                  <span>
                    <strong>{o.label}</strong> — $
                    {dollars(billed(o.renewal_cents))} every{' '}
                    {months === 1 ? '28 days' : `${o.interval_days} days`}
                    {months > 1 && <> (≈${perMonth}/mo)</>}
                    {memberNote}
                    {(savingsCents(o) ?? 0) > 0 && (
                      <span className="text-status-success">
                        {' '}
                        · save ${Math.round((savingsCents(o) ?? 0) / 100)} vs monthly
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
          {chosen && (
            <p className="type-label-sm text-ink-muted mt-8">
              Starting {effective}, you’ll be billed $
              {dollars(billed(chosen.renewal_cents))} every{' '}
              {chosen.interval_days} days{memberNote}.
            </p>
          )}
          <div className="flex gap-8 mt-12">
            <Button
              size="sm"
              disabled={!selected}
              onClick={() => {
                if (!selected || !chosen) return;
                // Billing change — demand an explicit confirmation so a
                // stray tap while browsing the account page can't silently
                // move someone to a different plan (caught in staging
                // testing 2026-07-14).
                const ok = window.confirm(
                  `Switch to the ${chosen.label} plan?\n\nStarting ${effective}, ` +
                    `you'll be billed $${dollars(billed(chosen.renewal_cents))} ` +
                    `every ${chosen.interval_days} days${memberNote}. Nothing changes about ` +
                    `your current cycle.`,
                );
                if (ok) onConfirm(selected);
              }}
              loading={loading}
            >
              Switch plan
            </Button>
            <Button size="sm" variant="ghost" onClick={onCancel}>
              Never mind
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function ReschedulePanel({
  loading,
  currentRenewalDate,
  onConfirm,
  onCancel,
}: {
  loading: boolean;
  currentRenewalDate: string | null;
  onConfirm: (date: string) => void;
  onCancel: () => void;
}) {
  const today = new Date();
  const earliestAllowed = addDays(today, RESCHEDULE_MIN_DAYS);
  const max = addDays(today, RESCHEDULE_MAX_DAYS);
  // Push-only: min = max(today+7, current_period_end). The backend
  // rejects pull-forward with a 400 cannot_pull_forward, so the
  // calendar should never show invalid dates to begin with.
  const currentRenewal = currentRenewalDate ? parseISO(currentRenewalDate) : null;
  const min =
    currentRenewal && currentRenewal > earliestAllowed
      ? currentRenewal
      : earliestAllowed;
  const [date, setDate] = useState(format(min, 'yyyy-MM-dd'));
  return (
    <div className="mt-12 bg-surface-subtle px-15 py-12">
      <p className="type-body-sm mb-10">
        Pick the next billing date.
      </p>
      <input
        type="date"
        min={format(min, 'yyyy-MM-dd')}
        max={format(max, 'yyyy-MM-dd')}
        value={date}
        onChange={(e) => setDate(e.target.value)}
        className=" border border-line-default px-10 py-8 type-body-sm"
      />
      <p className="type-label-sm text-ink-muted mt-8">
        Push your next bill back up to {RESCHEDULE_MAX_DAYS} days from today.
        {currentRenewal && currentRenewal > earliestAllowed && (
          <>
            {' '}
            Earliest available: {format(currentRenewal, 'MMM d, yyyy')} (your
            current renewal).
          </>
        )}{' '}
        To renew sooner, cancel and re-purchase.
      </p>
      <div className="flex gap-8 mt-12">
        <Button size="sm" onClick={() => onConfirm(date)} loading={loading}>
          Set new date
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Never mind
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div>
      <dt className="type-label-sm text-ink-muted uppercase tracking-tight">
        {label}
      </dt>
      <dd className="type-body-md break-words">
        {value}
        {sub && (
          <span className="type-label-sm text-ink-muted ml-8">({sub})</span>
        )}
      </dd>
    </div>
  );
}
